import { useState, useCallback, useEffect, useRef } from 'react';
import { message } from 'antd';
import { p9410Api, DepartmentItem, QueryRequest, QueryResponse } from '../../../services/p9410Api';

export interface P9410State {
  loading: boolean;
  departments: DepartmentItem[];
  attendanceData: QueryResponse | null;
  selectedDept: string; // "" = all
  sortBy: string; // "employee" | "department"
  noData: boolean;
  currentTime: string;
}

export interface P9410Actions {
  selectDepartment: (deptCode: string) => void;
  toggleSort: () => void;
  refresh: () => void;
}

const AUTO_REFRESH_MS = 5 * 60 * 1000; // 5 minutes (BR-T01)

export function useP9410(): P9410State & P9410Actions {
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState<DepartmentItem[]>([]);
  const [attendanceData, setAttendanceData] = useState<QueryResponse | null>(null);
  const [selectedDept, setSelectedDept] = useState('');
  const [sortBy, setSortBy] = useState('employee');
  const [noData, setNoData] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date().toLocaleTimeString('zh-TW', { hour12: false }));

  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const clockTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Fetch departments on mount
  useEffect(() => {
    const fetchDepts = async () => {
      try {
        const depts = await p9410Api.getDepartments();
        setDepartments(depts);
      } catch {
        message.error('載入部門列表失敗');
      }
    };
    fetchDepts();
  }, []);

  // Clock timer (BR-T02): update every second
  useEffect(() => {
    clockTimerRef.current = setInterval(() => {
      setCurrentTime(new Date().toLocaleTimeString('zh-TW', { hour12: false }));
    }, 1000);
    return () => {
      if (clockTimerRef.current) clearInterval(clockTimerRef.current);
    };
  }, []);

  // Query function
  const runQuery = useCallback(async (deptCode: string, sort: string) => {
    setLoading(true);
    setNoData(false);
    try {
      const req: QueryRequest = {};
      if (deptCode) req.dept_code = deptCode;
      if (sort) req.sort_by = sort;

      const resp = await p9410Api.query(req);
      if (resp.data && resp.data.rows.length > 0) {
        setAttendanceData(resp.data);
        setNoData(false);
      } else {
        setAttendanceData(null);
        setNoData(true);
        message.info(resp.message || '查無符合條件的出勤資料!');
      }
    } catch {
      message.error('查詢失敗，請稍後再試');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial query on mount
  useEffect(() => {
    runQuery('', 'employee');
  }, [runQuery]);

  // Auto-refresh timer (BR-T01): every 5 minutes
  useEffect(() => {
    refreshTimerRef.current = setInterval(() => {
      runQuery(selectedDept, sortBy);
    }, AUTO_REFRESH_MS);
    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    };
  }, [selectedDept, sortBy, runQuery]);

  // Actions
  const selectDepartment = useCallback((deptCode: string) => {
    setSelectedDept(deptCode);
    runQuery(deptCode, sortBy);
  }, [sortBy, runQuery]);

  const toggleSort = useCallback(() => {
    const newSort = sortBy === 'employee' ? 'department' : 'employee';
    setSortBy(newSort);
    runQuery(selectedDept, newSort);
  }, [selectedDept, sortBy, runQuery]);

  const refresh = useCallback(() => {
    runQuery(selectedDept, sortBy);
  }, [selectedDept, sortBy, runQuery]);

  return {
    loading, departments, attendanceData, selectedDept, sortBy, noData, currentTime,
    selectDepartment, toggleSort, refresh,
  };
}
